import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  TextField,
  Box,
  IconButton,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePasskey } from '../contexts/PasskeyContext';
import {
  getJournalById,
  updateJournalEntry,
  deleteJournalEntry,
  getJournalLabels,
  JournalEntry,
} from '../services/journalService';
import Header from '../components/Header';
import NotebookEditor from '../components/NotebookEditor';
import LabelSelector from '../components/LabelSelector';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const JournalEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { passkey } = usePasskey();

  const [journal, setJournal] = useState<JournalEntry | null>(null);
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [labels, setLabels] = useState<string[]>([]);
  const [availableLabels, setAvailableLabels] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'dirty' | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadJournal();
    loadAvailableLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentUser, passkey]);

  const loadAvailableLabels = async () => {
    if (!currentUser) return;
    try {
      const fetchedLabels = await getJournalLabels(currentUser.uid);
      setAvailableLabels(fetchedLabels);
    } catch (error) {
      console.error('Error loading labels:', error);
    }
  };

  const loadJournal = async () => {
    if (!currentUser || !id || !passkey) return;

    try {
      setLoading(true);
      const fetchedJournal = await getJournalById(id, passkey);

      if (fetchedJournal) {
        setJournal(fetchedJournal);
        setTitle(fetchedJournal.title || '');
        setContent(fetchedJournal.content || '');
        setLabels(fetchedJournal.labels || []);
      } else {
        setSnackbar({ open: true, message: 'Journal not found', severity: 'error' });
        navigate('/journals');
      }
    } catch (error) {
      console.error('Error loading journal:', error);
      setSnackbar({ open: true, message: 'Error loading journal', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Auto-save function
  const autoSave = useCallback(
    async (updates: { title?: string; content?: string; labels?: string[] }) => {
      if (!id || !passkey) return;

      try {
        setSaveStatus('saving');
        await updateJournalEntry(id, updates, passkey);
        setSaveStatus('saved');

        setTimeout(() => {
          setSaveStatus(null);
        }, 2000);
      } catch (error) {
        console.error('Auto-save error:', error);
        setSaveStatus(null);
      }
    },
    [id, passkey]
  );

  // Debounced auto-save
  const debouncedAutoSave = useCallback(
    (updates: { title?: string; content?: string; labels?: string[] }) => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      setSaveStatus('dirty');

      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave(updates);
      }, 1000);
    },
    [autoSave]
  );

  // Auto-save on title change
  useEffect(() => {
    if (journal && id && title !== undefined && !journal.isLocked) {
      debouncedAutoSave({ title: title || 'Untitled Journal' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  // Auto-save on content change
  useEffect(() => {
    if (journal && id && content !== undefined && !journal.isLocked) {
      debouncedAutoSave({ content });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const handleDelete = async () => {
    if (!id) return;

    try {
      await deleteJournalEntry(id);
      setSnackbar({ open: true, message: 'Journal deleted', severity: 'success' });
      navigate('/journals');
    } catch (error) {
      console.error('Error deleting journal:', error);
      setSnackbar({ open: true, message: 'Error deleting journal', severity: 'error' });
    }
    setDeleteDialogOpen(false);
  };

  const handleLabelsChange = async (newLabels: string[]) => {
    setLabels(newLabels);

    if (id && passkey && !journal?.isLocked) {
      try {
        setSaveStatus('saving');
        await updateJournalEntry(id, { labels: newLabels }, passkey);
        setSaveStatus('saved');
        
        // Reload available labels
        loadAvailableLabels();

        setTimeout(() => {
          setSaveStatus(null);
        }, 2000);
      } catch (error) {
        console.error('Error saving labels:', error);
        setSaveStatus(null);
        setSnackbar({ open: true, message: 'Error saving labels', severity: 'error' });
      }
    }
  };

  const handleBack = () => {
    navigate('/journals');
  };

  if (loading) {
    return (
      <>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', mt: 10 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  // Show locked journal UI
  if (journal?.isLocked) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 10, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <LockIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              This Journal is Locked
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              This journal was encrypted with a different passkey and cannot be decrypted with your
              current passkey.
            </Typography>
            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => navigate('/journals')}>
                Go Back
              </Button>
              <Button variant="contained" color="error" onClick={() => setDeleteDialogOpen(true)}>
                Delete This Journal
              </Button>
            </Box>
          </Paper>
        </Container>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Locked Journal?</DialogTitle>
          <DialogContent>
            <Typography>This journal cannot be decrypted. Delete it permanently?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Header />

      <Container maxWidth="md" sx={{ mt: 10, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>

          {/* Labels in header */}
          <Box sx={{ flexGrow: 1, maxWidth: 400 }}>
            <LabelSelector
              availableLabels={availableLabels}
              selectedLabels={labels}
              onLabelsChange={handleLabelsChange}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {saveStatus === 'saved' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  color: 'white',
                }}
              >
                <CheckIcon sx={{ fontSize: 16 }} />
              </Box>
            )}
            {saveStatus === 'saving' && <CircularProgress size={20} />}

            <IconButton color="error" onClick={() => setDeleteDialogOpen(true)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        <Paper
          elevation={2}
          sx={{
            p: 3,
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(145deg, #2D3748 0%, #1A202C 100%)'
                : 'linear-gradient(145deg, #FFFEF0 0%, #FFF9E6 100%)',
          }}
        >
          <TextField
            fullWidth
            variant="standard"
            placeholder="Journal Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiInputBase-input': {
                fontSize: '2rem',
                fontWeight: 600,
                fontFamily: '"Caveat", cursive',
              },
            }}
          />

          <NotebookEditor
            content={content}
            onChange={setContent}
            placeholder="Start writing your journal..."
            paperColor="yellow"
          />
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Journal?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this journal? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default JournalEditor;

